import { 
    Chip,
    Dialog,
    DialogContent, 
    DialogTitle, 
    List, 
    makeStyles, 
    Paper, 
    Typography } from "@material-ui/core";
import React from "react";
import License, { SpectralService } from "spectral-core/models/License";
import intl from "react-intl-universal";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";

import { 
    Assignment, 
    AttachMoney, 
    CardTravel, 
    Comment, 
    Computer, 
    Event, 
    FormatListBulleted, 
    Functions, 
    People, 
    PermDeviceInformation, 
    PhonelinkLock } from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
    field: {
        display: "flex",
        gap: 8,
        alignItems: "center"
    },
    paper: {
        padding: 16, 
        marginBottom: 16
    }
}));

interface LicenseDetailsProps {
    open: boolean;
    license: License;
    onClose: () => void;
};

export default function LicenseDetailsDialog({
    open,
    license,
    onClose
}: LicenseDetailsProps) {
    const classes = useStyles();

    const users = license.users 
        ? JSON.parse(license.users) as string[] 
        : [];

    const devices = license.devices 
        ? JSON.parse(license.devices) as string[] 
        : [];
    
    const models = license.models
        ? JSON.parse(license.models) as string[]
        : [];

    return (
        <Dialog 
            open={open}
            onClose={onClose}
            maxWidth="sm" fullWidth>
                <DialogTitle style={{ fontWeight: 600, textAlign: "center" }}>
                    <Assignment style={{ marginRight: 8 }} />
                    {license?.name ?? license?.rowKey}
                </DialogTitle>

                <DialogContent dividers>
                    <Paper className={classes.paper}>
                        <GridContainer spacing={4}>
                            {/*@ts-ignore*/}
                            <GridItem xs={12}>
                                <Typography 
                                    variant="subtitle2" 
                                    className={classes.field}
                                    gutterBottom>
                                    <Comment />
                                    {intl.get("license.description")}
                                </Typography>
                            </GridItem>
                            {/*@ts-ignore*/}
                            <GridItem xs={12}>
                                <Typography variant="body2" paragraph>
                                    {license?.description || intl.get("license.noDescription")}
                                </Typography>
                            </GridItem>
                            {/*@ts-ignore*/}
                            <GridItem xs={6}>
                                <Typography 
                                    variant="subtitle2" 
                                    className={classes.field}
                                    gutterBottom>
                                    <AttachMoney />
                                    {intl.get("license.price")}
                                </Typography>

                                <Typography variant="body1" paragraph>
                                    {license.price}
                                </Typography>
                            </GridItem>

                            {/*@ts-ignore*/}
                            <GridItem xs={6}>
                                <Typography 
                                    variant="subtitle2" 
                                    className={classes.field}
                                    gutterBottom>
                                    <Event />
                                    {intl.get("license.expiresAt")}
                                </Typography>

                                <Typography variant="body1" paragraph>
                                    {new Date(license.expiresAt).toLocaleString().split(',')[0]}
                                </Typography>
                            </GridItem>
                        </GridContainer>
                    </Paper>

                    <Paper className={classes.paper}>
                        <GridContainer spacing={4}>
                            {/*@ts-ignore*/}
                            <GridItem xs={6}>
                                <Typography 
                                    variant="subtitle2" 
                                    className={classes.field}
                                    gutterBottom>
                                    <CardTravel /> 
                                    {intl.get("license.service")}
                                </Typography>
                            </GridItem>

                            {/*@ts-ignore*/}
                            <GridItem xs={6}>
                                <Typography 
                                    variant="subtitle2"
                                    className={classes.field}
                                    gutterBottom>
                                    <PhonelinkLock />
                                    {intl.get("license.deviceRestrict")}
                                </Typography>
                            </GridItem>

                            {/*@ts-ignore*/}
                            <GridItem xs={6}>
                                <Typography variant="body1" paragraph>
                                    {SpectralService[license.service]}
                                </Typography>
                            </GridItem>

                            {/*@ts-ignore*/}
                            <GridItem xs={6}>
                                <Typography 
                                    variant="body1" 
                                    paragraph>
                                    {license.isDeviceRestrict 
                                    ? intl.get("license.yes")
                                    : intl.get("license.no")}
                                </Typography>
                            </GridItem>

                            {/*@ts-ignore*/}
                        <GridItem xs={12}>
                            <Typography 
                                variant="subtitle2" 
                                className={classes.field}
                                gutterBottom>
                                <Functions />
                                {intl.get("license.modelRestrict")}
                            </Typography>
                        </GridItem>

                        {/*@ts-ignore*/}
                        <GridItem xs={12}>
                          <Typography variant="body1" paragraph>
                                {license.isModelRestrict 
                                ? intl.get("license.yes")
                                : intl.get("license.no")}
                            </Typography>
                        </GridItem>
                        </GridContainer>
                    </Paper>
                    
                    <Paper className={classes.paper}>
                        <GridContainer spacing={2}>
                            {/*@ts-ignore*/}
                            <GridItem xs={12}>
                                <Typography 
                                    variant="subtitle2"
                                    className={classes.field}
                                    gutterBottom>
                                    <People />
                                    {intl.get("license.users")}
                                </Typography>
                            </GridItem>

                            {/*@ts-ignore*/}
                            <GridItem xs={12}>
                                {users.length > 0 ? (
                                    <List>
                                        {users.map((u) => (
                                            <Chip label={u} key={u} style={{ margin: 2 }} />
                                        ))}
                                    </List>
                                ) : 
                                <Typography 
                                    variant="body1" 
                                    paragraph>
                                {intl.get("license.noUserRegistered")}
                                </Typography>}
                            </GridItem>
                        </GridContainer>
                    </Paper>

                    <Paper className={classes.paper}>
                        <GridContainer spacing={2}>
                            {/*@ts-ignore*/}
                            <GridItem xs={12}>
                                <Typography 
                                    variant="subtitle2" 
                                    className={classes.field}
                                    gutterBottom>
                                    <PermDeviceInformation />
                                    {intl.get("license.devices")}
                                </Typography>
                            </GridItem>

                            {/*@ts-ignore*/}
                            <GridItem xs={12}>
                                {devices.length > 0 ? (
                                    <List>
                                        {devices.map((d) => (
                                            <Chip label={d} style={{ margin: 2 }} />
                                        ))}
                                    </List>
                                ) :
                                <Typography 
                                    variant="body1" 
                                    paragraph>
                                {intl.get("license.noDeviceRegistered")}
                                </Typography>}
                            </GridItem>
                        </GridContainer>
                    </Paper>

                    <Paper className={classes.paper}>
                        <GridContainer spacing={4}>
                            {/*@ts-ignore*/}
                            <GridItem xs={12}>
                                <Typography 
                                    variant="subtitle2"
                                    className={classes.field}
                                    gutterBottom>
                                    <FormatListBulleted />
                                    {intl.get("license.models")}
                                </Typography>
                            </GridItem>

                            {/*@ts-ignore*/}
                            <GridItem xs={12}>
                            <List>
                                {models.length > 0 ? (
                                    models.map((m) => (
                                        <Chip label={m} style={{ margin: 2 }} />
                                    ))
                                ): 
                                <Typography 
                                    variant="body1" 
                                    paragraph>
                                {intl.get("license.noModelRegistered")}
                                </Typography>}
                            </List>
                            </GridItem>
                        </GridContainer>
                    </Paper>
                    
                    <Paper className={classes.paper}>
                        <GridContainer spacing={4}>
                            {/*@ts-ignore*/}
                            <GridItem xs={12}>
                                <Typography 
                                    variant="subtitle2"
                                    className={classes.field}
                                    gutterBottom>
                                    <Computer />
                                    {intl.get("license.computerMac")}
                                </Typography>
                            </GridItem>

                            {/*@ts-ignore*/}
                            <GridItem xs={12}>
                            <Typography variant="body1" paragraph>
                                    {license.computerMac 
                                        ? license.computerMac 
                                        : intl.get("license.noComputerMacRegistered")}
                                </Typography>
                            </GridItem>
                        </GridContainer>
                    </Paper>

                </DialogContent>
            
        </Dialog>
    );
}